import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useIsAdminView } from 'hooks';
import { Flex, Loading, Thumbnail, Button } from 'design-system-web';
import { AdminPage, AddAttachment } from 'components';
import { Alert, AlertType, BreadCrumbs } from 'components/lds';
import { H3, S, P, H4 } from 'components/typography';
import { Channel } from 'lane-shared/types/ChannelType';
import { UserType } from 'lane-shared/types/User';
import {
  LONG_MONTH_DATE_YEAR,
  SHORT_TIME_WITH_TZ,
} from 'lane-shared/helpers/constants/dates';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { dateFormatter, capitalize } from 'lane-shared/helpers/formatters';
import { routes } from 'lane-shared/config';
import { EntityTypeEnum, AttachmentVariantEnum } from 'lane-shared/types/attachment';
import { formatFullName } from 'formatters';
import { useVisitorWatchlist } from 'react-hooks';
import { convertToUUID } from 'uuid-encoding';
import { useAttachmentByEntityData } from 'hooks/useAttachmentByEntityData';
import { hasPermission } from 'lane-shared/helpers';
import { PERMISSION_KEYS } from 'constants-permissions';
import { VisitorWatchlistForm } from '../../components/VisitorWatchlistForm/VisitorWatchlistForm';

import styles from './styles.scss';

export function WatchlistDetailsPage({
  channel,
  user,
}: {
  channel: Channel;
  user: UserType;
}) {
  const { t } = useTranslation();
  const [, channelSlug] = useIsAdminView();
  const { watchlistId } = useParams<{ watchlistId: string }>();
  const [isEditMode, setIsEditMode] = useState(false);

  const timeZone = getTimeZoneByGeoLocation({
    latitude: channel?.address?.geo?.[1],
    longitude: channel?.address?.geo?.[0],
  });

  const {
    data: watchlistData,
    loading: watchlistLoading,
    error: watchlistError,
    refetch: refetchWatchlist,
  } = useVisitorWatchlist(watchlistId, convertToUUID(channel._id));

  const { attachments, fetchAttachments } = useAttachmentByEntityData({
    entityId: watchlistId,
    editMode: false,
  });

  const handleEditSuccess = () => {
    setIsEditMode(false);
    // Refetch watchlist and attachments to get the latest data after edit
    refetchWatchlist();
    fetchAttachments();
  };

  const handleEditClick = () => {
    if (isAdmin || hasManageWatchlistPermission) {
      setIsEditMode(true);
    }
  };

  const getTitle = (): string => {
    return formatFullName(
      capitalize(watchlistData?.visitorMatchingInfo.firstName?.primaryValue || ''),
      capitalize(watchlistData?.visitorMatchingInfo.lastName?.primaryValue || '')
    );
  };

  const getCreatedOnInfo = (): string => {
    if (!watchlistData) return '';

    const createdDate = dateFormatter(
      watchlistData.createdAt,
      LONG_MONTH_DATE_YEAR,
      timeZone
    );
    const createdTime = dateFormatter(
      watchlistData.createdAt,
      SHORT_TIME_WITH_TZ,
      timeZone
    );
    const createdBy =
      watchlistData.createdByName ||
      t('web.admin.channel.visitor.watchlist.details.unknown');

    return t('web.admin.channel.visitor.watchlist.details.createdOn', {
      createdDate,
      createdTime,
      createdBy,
    });
  };

  const getUpdatedOnInfo = (): string => {
    if (!watchlistData) return '';

    const updatedDate = dateFormatter(
      watchlistData.updatedAt,
      LONG_MONTH_DATE_YEAR,
      timeZone
    );
    const updatedTime = dateFormatter(
      watchlistData.updatedAt,
      SHORT_TIME_WITH_TZ,
      timeZone
    );
    const updatedBy =
      watchlistData.updatedByName ||
      t('web.admin.channel.visitor.watchlist.details.unknown');

    return t('web.admin.channel.visitor.watchlist.details.updatedOn', {
      updatedDate,
      updatedTime,
      updatedBy,
    });
  };

  const renderAttachments = (): React.ReactNode | null => {
    if (!attachments || attachments.length === 0) {
      return null;
    }

    return (
      <Flex
          direction="column"
          width="full"
      >
          <Flex direction="column">
            <H4 mb={1} mt={1} className={styles.attachmentsTitle}>
              {t('web.admin.channel.visitor.watchlist.details.attachments')}
            </H4>
            <AddAttachment
              entityId={watchlistId}
              entityType={EntityTypeEnum.VisitorAttachment}
              editMode={false}
              preventDelete
              variant={AttachmentVariantEnum.Visitor}
              canAddAttachments={false}
            />
          </Flex>
        </Flex>
    );
  };

  const getVisitorInfo = (
    field: 'firstName' | 'lastName' | 'email' | 'phone'
  ): string => {
    const info = watchlistData?.visitorMatchingInfo[field];

    if (!info) {
      return '-';
    }

    const primary = info.primaryValue;
    const alternates = info.alternateValues || [];

    const values = [primary, ...alternates].filter(Boolean);

    if (values.length === 0) {
      return '-';
    }
  
    if (field === 'firstName' || field === 'lastName') {
      return values.map(value => capitalize(value)).join(', ');
    }
    
    return values.join(', ');
  };

  // Check if user has view permission for watchlist
  const hasViewWatchlistPermission =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_VIEW,
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_MANAGE,
        PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_CONFIGURE,
      ],
      channel._id
    );

  // Check if user is admin (admin users can always edit)
  const isAdmin =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_ADMIN],
      channel._id
    );

  // Check if user has manage permission (non-admin users with manage permission can edit)
  const hasManageWatchlistPermission =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_VISITORS_WATCHLIST_MANAGE],
      channel._id
    );

  if (watchlistLoading) {
    return (
      <AdminPage>
        <Loading testId="loading" />
      </AdminPage>
    );
  }

  if (watchlistError || !watchlistData) {
    return (
      <AdminPage>
        <Alert type={AlertType.error} fullWidth>
          {t('web.admin.channel.visitor.watchlist.details.error.notFound')}
        </Alert>
      </AdminPage>
    );
  }

  // Check if user has view permission to access the details page
  if (!hasViewWatchlistPermission) {
    return (
      <AdminPage>
        <Alert type={AlertType.error} fullWidth>
          {t('web.admin.channel.visitor.watchlist.permission.view.required')}
        </Alert>
      </AdminPage>
    );
  }

  // Show edit form when in edit mode (for admin users or users with manage permission)
  if (isEditMode && (isAdmin || hasManageWatchlistPermission)) {
    return (
      <AdminPage>
        <Flex direction="column" gap={5}>
          <BreadCrumbs
            links={[
              {
                label: t('web.admin.channel.visitor.watchlist.breadcrumb'),
                url: routes.visitorManagementWatchlist.replace(
                  ':id',
                  channelSlug || ''
                ),
              },
              {
                label: t('web.admin.channel.visitor.watchlist.details'),
                url: routes.visitorManagementWatchlistDetails.replace(
                  ':id',
                  channelSlug || ''
                ).replace(':watchlistId', watchlistId || ''),
              },
              {
                label: t('web.admin.channel.visitor.watchlist.edit'),
              },
            ]}
          />
          <Flex direction="row" justify="space-between" align="center">
            <H3>{t('web.admin.channel.visitor.watchlist.edit.title')}</H3>
          </Flex>
          <VisitorWatchlistForm
            isEditMode
            watchlist={watchlistData}
            onEditSuccess={handleEditSuccess}
          />
        </Flex>
      </AdminPage>
    );
  }

  // Show details view
  return (
    <AdminPage>
      <Flex direction="column">
        <Flex direction="column" gap={5}>
          <BreadCrumbs
            links={[
              {
                label: t('web.admin.channel.visitor.watchlist.breadcrumb'),
                url: routes.visitorManagementWatchlist.replace(
                  ':id',
                  channelSlug || ''
                ),
              },
              {
                label: t('web.admin.channel.visitor.watchlist.details'),
              },
            ]}
          />
          <Flex direction="row" justify="space-between" align="center">
            <Flex direction="column" gap={2}>
              <H3>{getTitle()}</H3>
              <S variant="secondary">{getCreatedOnInfo()}</S>
              {watchlistData.updatedAt !== watchlistData.createdAt && (
                <S variant="secondary">{getUpdatedOnInfo()}</S>
              )}
            </Flex>
            <Button
              size="large"
              variant="secondary"
              disabled={!isAdmin && !hasManageWatchlistPermission}
              onClick={handleEditClick}
            >
              {t('web.admin.channel.visitor.watchlist.edit.button')}
            </Button>
          </Flex>
        </Flex>

        <Flex direction="column" gap={5} className={styles.detailsContainer}>
          <H4>{t('web.admin.channel.visitor.watchlist.details.title')}</H4>

          <Flex direction="column" gap={5} className={styles.rowsContainer}>
            <Flex direction="row" className={styles.row}>
              <Flex direction="column" className={styles.rowItem}>
                <S mb={1}>
                  {t('web.admin.channel.visitor.watchlist.details.firstNames')}
                </S>
                <P>{getVisitorInfo('firstName')}</P>
              </Flex>
              <Flex direction="column" className={styles.rowItem}>
                <S mb={1}>
                  {t('web.admin.channel.visitor.watchlist.details.lastNames')}
                </S>
                <P>{getVisitorInfo('lastName')}</P>
              </Flex>
            </Flex>

            <Flex direction="row" className={styles.row}>
              <Flex direction="column" className={styles.rowItem}>
                <S mb={1}>
                  {t('web.admin.channel.visitor.watchlist.details.emails')}
                </S>
                <P>{getVisitorInfo('email')}</P>
              </Flex>
              <Flex direction="column" className={styles.rowItem}>
                <S mb={1}>
                  {t('web.admin.channel.visitor.watchlist.details.phoneNumber')}
                </S>
                <P>{getVisitorInfo('phone')}</P>
              </Flex>
            </Flex>

            <Flex direction="row" className={styles.row}>
              <Flex direction="column" className={styles.rowItem}>
                <S mb={1}>
                  {t('web.admin.channel.visitor.watchlist.details.notes')}
                </S>
                <P className={styles.notes}>{watchlistData.notes || '-'}</P>
              </Flex>
            </Flex>

            <Flex direction="row" className={styles.row}>
              <Flex direction="column" className={styles.rowItem}>
                <S mb={1}>
                  {t('web.admin.channel.visitor.watchlist.details.reportedBy')}
                </S>
                <Flex gap={2} align="center">
                  {watchlistData.reportedBy && <Thumbnail name={watchlistData.reportedBy} />}
                  <P>{watchlistData.reportedBy || '-'}</P>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        {renderAttachments()}
      </Flex>
    </AdminPage>
  );
}
